/**
 * SharePoint Dashboard Page
 * Provides an overview of all SharePoint image metadata jobs with aggregate statistics
 */

import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Plus,
  RefreshCw,
  Play,
  Pause,
  BarChart3,
  Clock,
  Image,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  FolderOpen,
  Activity,
  List,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  fetchDashboardSummary,
  fetchAggregateStatistics,
  fetchRecentActivity,
  bulkEnableJobs,
  bulkDisableJobs,
  bulkTriggerJobs,
  type DashboardSummary,
  type AggregateStatistics,
  type RecentActivity,
} from "@/lib/sharepoint-api";
import { SharePointLayout } from "./layout";

export default function SharePointDashboardPage() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [statistics, setStatistics] = useState<AggregateStatistics | null>(null);
  const [activity, setActivity] = useState<RecentActivity | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const { toast } = useToast();

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const [summaryData, statsData, activityData] = await Promise.all([
        fetchDashboardSummary(),
        fetchAggregateStatistics(),
        fetchRecentActivity(),
      ]);
      setSummary(summaryData);
      setStatistics(statsData);
      setActivity(activityData);
    } catch (error: any) {
      toast({
        title: "Error loading dashboard",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const toggleJobSelection = (jobId: string) => {
    const newSelected = new Set(selectedJobs);
    if (newSelected.has(jobId)) {
      newSelected.delete(jobId);
    } else {
      newSelected.add(jobId);
    }
    setSelectedJobs(newSelected);
  };

  const selectAllJobs = () => {
    if (statistics?.jobs) {
      const allJobIds = new Set(statistics.jobs.map(j => j.jobId));
      setSelectedJobs(allJobIds);
    }
  };

  const clearSelection = () => {
    setSelectedJobs(new Set());
  };

  const handleBulkEnable = async () => {
    if (selectedJobs.size === 0) return;
    try {
      setBulkActionLoading(true);
      const result = await bulkEnableJobs(Array.from(selectedJobs));
      toast({
        title: "Jobs enabled",
        description: `Successfully enabled ${result.summary.succeeded} jobs`,
      });
      clearSelection();
      loadDashboard();
    } catch (error: any) {
      toast({
        title: "Error enabling jobs",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDisable = async () => {
    if (selectedJobs.size === 0) return;
    try {
      setBulkActionLoading(true);
      const result = await bulkDisableJobs(Array.from(selectedJobs));
      toast({
        title: "Jobs disabled",
        description: `Successfully disabled ${result.summary.succeeded} jobs`,
      });
      clearSelection();
      loadDashboard();
    } catch (error: any) {
      toast({
        title: "Error disabling jobs",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkTrigger = async () => {
    if (selectedJobs.size === 0) return;
    try {
      setBulkActionLoading(true);
      const result = await bulkTriggerJobs(Array.from(selectedJobs));
      toast({
        title: "Jobs triggered",
        description: `Successfully triggered ${result.summary.succeeded} jobs`,
      });
      clearSelection();
      loadDashboard();
    } catch (error: any) {
      toast({
        title: "Error triggering jobs",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleString();
  };

  const formatRelativeTime = (dateString?: string) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    return `${diffDays} days ago`;
  };

  if (loading) {
    return (
      <SharePointLayout>
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
            <p className="text-muted-foreground">Loading dashboard...</p>
          </div>
        </div>
      </SharePointLayout>
    );
  }

  return (
    <SharePointLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">SharePoint Image Dashboard</h1>
            <p className="text-muted-foreground">
              Monitor and manage image metadata extraction across SharePoint libraries
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadDashboard}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Link href="/sharepoint/jobs">
              <Button variant="outline">
                <List className="h-4 w-4 mr-2" />
                View All Jobs
              </Button>
            </Link>
            <Link href="/sharepoint/jobs/new">
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                New Job
              </Button>
            </Link>
          </div>
        </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Jobs Overview */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Jobs</CardTitle>
            <FolderOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.jobs.total || 0}</div>
            <p className="text-xs text-muted-foreground">
              {summary?.jobs.enabled || 0} enabled, {summary?.jobs.disabled || 0} disabled
            </p>
          </CardContent>
        </Card>

        {/* Sites Overview */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">SharePoint Sites</CardTitle>
            <Image className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.sites.total || 0}</div>
            <p className="text-xs text-muted-foreground">
              {summary?.sites.uniqueSiteIds.length || 0} unique sites
            </p>
          </CardContent>
        </Card>

        {/* Images Processed */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Images Processed</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.quality.totalProcessed || 0}</div>
            <p className="text-xs text-muted-foreground">
              {summary?.quality.passRate || 0}% pass rate
            </p>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Recent Activity</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.recentActivity.executionsLast24h || 0}</div>
            <p className="text-xs text-muted-foreground">
              executions in last 24h
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quality Overview */}
      {summary && summary.quality.totalProcessed > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Quality Overview</CardTitle>
            <CardDescription>Image quality validation results across all jobs</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Pass */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span className="text-sm font-medium">Pass</span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {summary.quality.pass} images ({Math.round((summary.quality.pass / summary.quality.totalProcessed) * 100)}%)
                  </span>
                </div>
                <Progress 
                  value={(summary.quality.pass / summary.quality.totalProcessed) * 100} 
                  className="h-2 bg-gray-200"
                />
              </div>

              {/* Warning */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-500" />
                    <span className="text-sm font-medium">Warning</span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {summary.quality.warning} images ({Math.round((summary.quality.warning / summary.quality.totalProcessed) * 100)}%)
                  </span>
                </div>
                <Progress 
                  value={(summary.quality.warning / summary.quality.totalProcessed) * 100} 
                  className="h-2 bg-gray-200"
                />
              </div>

              {/* Fail */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-500" />
                    <span className="text-sm font-medium">Fail</span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {summary.quality.fail} images ({Math.round((summary.quality.fail / summary.quality.totalProcessed) * 100)}%)
                  </span>
                </div>
                <Progress 
                  value={(summary.quality.fail / summary.quality.totalProcessed) * 100} 
                  className="h-2 bg-gray-200"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Jobs with Statistics */}
      {statistics && statistics.jobs.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Job Statistics</CardTitle>
                <CardDescription>Quality metrics per job with bulk actions</CardDescription>
              </div>
              {selectedJobs.size > 0 && (
                <div className="flex gap-2">
                  <span className="text-sm text-muted-foreground self-center">
                    {selectedJobs.size} selected
                  </span>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleBulkEnable}
                    disabled={bulkActionLoading}
                  >
                    <Play className="h-4 w-4 mr-1" />
                    Enable
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleBulkDisable}
                    disabled={bulkActionLoading}
                  >
                    <Pause className="h-4 w-4 mr-1" />
                    Disable
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={handleBulkTrigger}
                    disabled={bulkActionLoading}
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Run Now
                  </Button>
                  <Button size="sm" variant="ghost" onClick={clearSelection}>
                    Clear
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox 
                      checked={selectedJobs.size === statistics.jobs.length && statistics.jobs.length > 0}
                      onCheckedChange={(checked) => checked ? selectAllJobs() : clearSelection()}
                    />
                  </TableHead>
                  <TableHead>Job Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sites</TableHead>
                  <TableHead className="text-center">Total</TableHead>
                  <TableHead className="text-center">
                    <span className="text-green-600">Pass</span>
                  </TableHead>
                  <TableHead className="text-center">
                    <span className="text-yellow-600">Warning</span>
                  </TableHead>
                  <TableHead className="text-center">
                    <span className="text-red-600">Fail</span>
                  </TableHead>
                  <TableHead className="text-right">Pass Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {statistics.jobs.map((job) => (
                  <TableRow key={job.jobId}>
                    <TableCell>
                      <Checkbox 
                        checked={selectedJobs.has(job.jobId)}
                        onCheckedChange={() => toggleJobSelection(job.jobId)}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      <Link href={`/sharepoint/jobs/edit/${job.jobId}`} className="hover:underline">
                        {job.jobName}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge variant={job.isEnabled ? "default" : "secondary"}>
                        {job.isEnabled ? "Enabled" : "Disabled"}
                      </Badge>
                    </TableCell>
                    <TableCell>{job.siteCount}</TableCell>
                    <TableCell className="text-center">{job.quality.total}</TableCell>
                    <TableCell className="text-center text-green-600">{job.quality.pass}</TableCell>
                    <TableCell className="text-center text-yellow-600">{job.quality.warning}</TableCell>
                    <TableCell className="text-center text-red-600">{job.quality.fail}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant={job.quality.passRate >= 80 ? "default" : job.quality.passRate >= 50 ? "secondary" : "destructive"}>
                        {job.quality.passRate}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Recent Executions */}
      {activity && activity.recentExecutions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Recent Executions</CardTitle>
            <CardDescription>
              {activity.metrics.executionsLast7d} executions in the last 7 days
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job Name</TableHead>
                  <TableHead>Executed At</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activity.recentExecutions.slice(0, 10).map((exec, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{exec.jobName}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span>{formatRelativeTime(exec.executedAt)}</span>
                        <span className="text-xs text-muted-foreground">
                          ({formatDate(exec.executedAt)})
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={
                        exec.status === 'completed' ? 'default' :
                        exec.status === 'failed' ? 'destructive' :
                        exec.status === 'running' ? 'secondary' :
                        'outline'
                      }>
                        {exec.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {(!statistics || statistics.jobs.length === 0) && !loading && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Image className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No SharePoint Jobs Configured</h3>
            <p className="text-muted-foreground mb-4">
              Get started by creating your first SharePoint image metadata extraction job.
            </p>
            <Link href="/sharepoint/jobs/new">
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create First Job
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}
      </div>
    </SharePointLayout>
  );
}
